import React from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Globe, Sun, Moon, LogOut, Wallet, User } from "lucide-react";

export default function Header({ onProfile }) {
  const { t, lang, toggleLang, theme, toggleTheme } = useApp();
  const { user, logout } = useAuth();

  return (
    <header
      data-testid="app-header"
      className="pt-5 pb-3 px-5 flex items-center justify-between sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/40"
    >
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-700 grid place-items-center shadow-md shadow-emerald-900/30">
          <Wallet className="w-5 h-5 text-white" />
        </div>
        <div className="leading-none">
          <span className="text-lg font-extrabold tracking-tight">
            Sb<span className="text-primary">Pay</span>
          </span>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {t.greeting}, {user?.name?.split(" ")[0]}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        <button
          data-testid="language-switch-toggle"
          onClick={toggleLang}
          className="flex items-center gap-1 text-sm font-bold px-2.5 h-9 rounded-full bg-muted hover:bg-muted/70 transition-colors"
        >
          <Globe className="w-4 h-4" />
          {lang === "ar" ? "EN" : "ع"}
        </button>
        <button
          data-testid="theme-toggle"
          onClick={toggleTheme}
          className="w-9 h-9 grid place-items-center rounded-full bg-muted hover:bg-muted/70 transition-colors"
        >
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="profile-menu-trigger" className="ms-0.5">
              <Avatar className="w-9 h-9 border-2 border-primary/30">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary/15 text-primary font-bold text-sm">
                  {user?.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={lang === "ar" ? "start" : "end"} className="w-52">
            <div className="px-2 py-1.5">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground">@{user?.handle}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-profile" onClick={onProfile} className="cursor-pointer">
              <User className="w-4 h-4 me-2" /> {t.profile}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer text-destructive focus:text-destructive">
              <LogOut className="w-4 h-4 me-2" /> {t.logout}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
